"use client"

import React from 'react'
import { cn } from '../../lib/utils'
import { UIAvatar, UIAvatarFallback, UIAvatarImage } from '../display/ui-avatar'
import { UIBadge } from '../display/ui-badge'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import { UIButton } from '../buttons/ui-button'
import { formatDistanceToNow } from 'date-fns'
import { ko, enUS } from 'date-fns/locale'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import { createMarkdownComponents } from '../../lib/markdown-components'

// 표준 GitHub highlight.js 테마 - 라이트/다크 모드별 조건부 import
import 'highlight.js/styles/github.css'

// 메시지 액션 인터페이스
export interface UIMessageAction {
  id: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
  onClick: () => void
  variant?: 'default' | 'outline' | 'ghost'
  disabled?: boolean
}

// 메시지 기본 인터페이스
export interface UIMessageProps {
  // 메시지 기본 정보
  id: string
  content: string
  role: 'user' | 'assistant' | 'system'
  timestamp?: Date

  // UI 커스터마이징
  variant?: 'default' | 'detailed'
  showAvatar?: boolean
  showTimestamp?: boolean
  showRole?: boolean
  enableMarkdown?: boolean

  // 고급 기능
  isTyping?: boolean
  isStreaming?: boolean
  actions?: UIMessageAction[]
  metadata?: Record<string, any>

  // 아바타 설정
  avatar?: {
    src?: string
    fallback?: string
    alt?: string
  }

  // 스타일링
  className?: string
  contentClassName?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 콜백
  onContentClick?: () => void
  onAvatarClick?: () => void
}

/**
 * UIMessage - AI 대화의 개별 메시지를 표시하는 컴포넌트
 * shadcn.io/ai Message 컴포넌트 기반으로 구현
 */
export const UIMessage = React.forwardRef<HTMLDivElement, UIMessageProps>(
  ({
    id,
    content,
    role,
    timestamp,
    variant = 'default',
    showAvatar = true,
    showTimestamp = true,
    showRole = false,
    enableMarkdown = true,
    isTyping = false,
    isStreaming = false,
    actions = [],
    metadata,
    avatar,
    className,
    contentClassName,
    ariaLabel,
    locale = 'ko',
    onContentClick,
    onAvatarClick,
    ...props
  }, ref) => {
    // 역할별 기본 설정
    const roleConfig = {
      user: {
        label: locale === 'ko' ? '사용자' : 'User',
        bgColor: 'bg-primary',
        textColor: 'text-primary-foreground',
        alignment: 'justify-end',
        avatarFallback: 'U',
        bubbleStyle: cn(
          // Light mode: solid background for high contrast
          'bg-primary text-primary-foreground',
          // Border for depth
          'border border-primary/30',
          // Shadow hierarchy
          'shadow-lg shadow-primary/[0.15]',
          // Dark mode: ensure contrast
          'dark:bg-primary dark:text-primary-foreground dark:border-primary/40 dark:shadow-primary/[0.25]'
        ),
      },
      assistant: {
        label: locale === 'ko' ? 'AI 어시스턴트' : 'AI Assistant',
        bgColor: 'bg-muted',
        textColor: 'text-foreground',
        alignment: 'justify-start',
        avatarFallback: 'AI',
        bubbleStyle: cn(
          // Light mode: higher opacity for better contrast
          'bg-card/85 text-foreground',
          // Subtle border
          'border border-border/60',
          // Layered shadows
          'shadow-xl shadow-black/[0.04]',
          // Dark mode: improved contrast for readability
          'dark:bg-card/75 dark:border-border/70 dark:shadow-black/[0.12]'
        ),
      },
      system: {
        label: locale === 'ko' ? '시스템' : 'System',
        bgColor: 'bg-secondary',
        textColor: 'text-secondary-foreground',
        alignment: 'justify-center',
        avatarFallback: 'S',
        bubbleStyle: cn(
          // Light mode: higher opacity for contrast
          'bg-secondary/80 text-secondary-foreground',
          // Border
          'border border-secondary/50',
          // Shadow
          'shadow-lg shadow-black/[0.02]',
          // Dark mode: ensure readability
          'dark:bg-secondary/70 dark:border-secondary/60 dark:shadow-black/[0.08]'
        ),
      },
    }

    const config = roleConfig[role]
    const dateLocale = locale === 'ko' ? ko : enUS

    // 마크다운 컴포넌트 설정 - 공유 유틸리티 사용
    const markdownComponents = createMarkdownComponents(locale)

    // 타이밍 표시 포맷
    const formatTimestamp = (date: Date) => {
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale: dateLocale
      })
    }

    // 메시지 콘텐츠 컴포넌트
    const MessageContent = () => (
      <UIBox
        className={cn(
          "relative max-w-[80%] break-words",
          variant === 'detailed' && "max-w-[85%]"
        )}
      >
        {/* 역할 표시 */}
        {showRole && (
          <UIBox className="mb-2">
            <UIBadge variant="outline">
              <UITypography
                as="span"
                variant="caption"
                className="drop-shadow-sm leading-relaxed"
              >
                {config.label}
              </UITypography>
            </UIBadge>
          </UIBox>
        )}

        {/* 메시지 버블 */}
        <UIBox
          className={cn(
            "rounded-xl px-4 py-3 transition-all duration-300 ease-out",
            config.bubbleStyle,
            variant === 'detailed' && "px-5 py-4",
            role === 'system' && "text-center italic",
            isStreaming && "animate-pulse",
            onContentClick && [
              "cursor-pointer group",
              "hover:-translate-y-0.5 hover:scale-[1.005]",
              "hover:shadow-2xl hover:shadow-black/[0.04] dark:hover:shadow-black/[0.15]"
            ],
            contentClassName
          )}
          onClick={onContentClick}
        >
          {/* 타이핑 인디케이터 */}
          {isTyping ? (
            <UIBox className="flex items-center space-x-1">
              <UIBox className="flex space-x-1">
                <UIBox className="w-2 h-2 bg-current rounded-full animate-bounce opacity-75" />
                <UIBox className="w-2 h-2 bg-current rounded-full animate-bounce delay-100 opacity-75" />
                <UIBox className="w-2 h-2 bg-current rounded-full animate-bounce delay-200 opacity-75" />
              </UIBox>
              <UITypography
                as="span"
                size="sm"
                className="ml-2 opacity-70 drop-shadow-sm leading-relaxed"
              >
                {locale === 'ko' ? '입력 중...' : 'Typing...'}
              </UITypography>
            </UIBox>
          ) : enableMarkdown ? (
            <UIBox className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            </UIBox>
          ) : (
            <UITypography
              as="div"
              className="whitespace-pre-wrap leading-relaxed"
            >
              {content}
            </UITypography>
          )}

          {/* 스트리밍 인디케이터 */}
          {isStreaming && !isTyping && (
            <UIBox className="inline-block w-2 h-4 bg-current animate-pulse ml-1 opacity-60" />
          )}
        </UIBox>

        {/* 메타데이터 표시 */}
        {variant === 'detailed' && metadata && Object.keys(metadata).length > 0 && (
          <UIBox className="mt-2">
            {Object.entries(metadata).map(([key, value]) => (
              <UITypography
                key={key}
                as="div"
                variant="caption"
                color="muted"
                className="inline-block mr-3 drop-shadow-sm leading-relaxed"
              >
                <UITypography as="span" weight="medium">{key}:</UITypography> {String(value)}
              </UITypography>
            ))}
          </UIBox>
        )}

        {/* 타임스탬프 */}
        {showTimestamp && timestamp && (
          <UITypography
            as="div"
            variant="caption"
            color="default"
            className={cn(
              "mt-1 drop-shadow-sm leading-relaxed transition-colors duration-300 ease-out opacity-70",
              role === 'user' && "text-right",
              role === 'system' && "text-center"
            )}
          >
            {formatTimestamp(timestamp)}
          </UITypography>
        )}

        {/* 액션 버튼들 */}
        {actions.length > 0 && (
          <UIBox className={cn(
            "mt-2 flex flex-wrap gap-1",
            role === 'user' && "justify-end",
            role === 'system' && "justify-center"
          )}>
            {actions.map((action) => (
              <UIButton
                key={action.id}
                onClick={action.onClick}
                disabled={action.disabled}
                variant={action.variant || "outline"}
                size="sm"
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-1 rounded",
                  "transition-colors"
                )}
              >
                {action.icon && <action.icon className="w-3 h-3" />}
                <UITypography
                  as="span"
                  variant="caption"
                  className="drop-shadow-sm leading-relaxed"
                >
                  {action.label}
                </UITypography>
              </UIButton>
            ))}
          </UIBox>
        )}
      </UIBox>
    )

    return (
      <UIBox
        ref={ref}
        role="article"
        className={cn(
          "flex w-full gap-3 mb-4 px-4",
          config.alignment,
          variant === 'detailed' && "mb-6 gap-4",
          role === 'system' && "justify-center",
          className
        )}
        aria-label={ariaLabel || `${config.label} 메시지: ${content.slice(0, 50)}...`}
        {...props}
      >
        {/* 아바타 (사용자와 어시스턴트만) */}
        {showAvatar && role !== 'system' && (
          <UIBox className={cn(
            "flex-shrink-0",
            role === 'user' && "order-2"
          )}>
            <UIAvatar
              className={cn(
                "w-8 h-8",
                variant === 'detailed' && "w-10 h-10",
                onAvatarClick && "cursor-pointer hover:opacity-80 transition-opacity"
              )}
              onClick={onAvatarClick}
            >
              <UIAvatarImage
                src={avatar?.src}
                alt={avatar?.alt || `${config.label} 아바타`}
              />
              <UIAvatarFallback
                variant={role === 'user' ? 'user' : 'assistant'}
                size={variant === 'detailed' ? 'lg' : 'md'}
              >
                {avatar?.fallback || config.avatarFallback}
              </UIAvatarFallback>
            </UIAvatar>
          </UIBox>
        )}

        {/* 메시지 콘텐츠 */}
        <UIBox className={cn(
          "min-w-0",
          role === 'assistant' && "flex-1",
          role === 'user' && "order-1 flex justify-end"
        )}>
          <MessageContent />
        </UIBox>
      </UIBox>
    )
  }
)

UIMessage.displayName = "UIMessage"

// 편의 컴포넌트들
export const UIUserMessage = React.forwardRef<HTMLDivElement, Omit<UIMessageProps, 'role'>>(
  (props, ref) => <UIMessage ref={ref} role="user" {...props} />
)

export const UIAssistantMessage = React.forwardRef<HTMLDivElement, Omit<UIMessageProps, 'role'>>(
  (props, ref) => <UIMessage ref={ref} role="assistant" {...props} />
)

export const UISystemMessage = React.forwardRef<HTMLDivElement, Omit<UIMessageProps, 'role'>>(
  (props, ref) => <UIMessage ref={ref} role="system" showAvatar={false} {...props} />
)

UIUserMessage.displayName = "UIUserMessage"
UIAssistantMessage.displayName = "UIAssistantMessage"
UISystemMessage.displayName = "UISystemMessage"